import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Eye, Mail, RefreshCw, ExternalLink, AlertCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import InstitutionalHero from './ui/InstitutionalHero';
import { getToken } from '../services/tokenService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailPreview = () => {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/email-previews`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setTemplates(data.templates || []);
      if (data.templates?.length && !selected) {
        setSelected(data.templates[0].key);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur chargement des templates');
    }
  };

  const fetchPreview = async (key) => {
    if (!key) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/admin/email-previews/${key}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        responseType: 'text',
      });
      setHtml(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur rendu du template');
      setHtml('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (selected) fetchPreview(selected);
  }, [selected]);

  const currentMeta = useMemo(
    () => templates.find((t) => t.key === selected),
    [templates, selected]
  );

  return (
    <div className="space-y-6">
      <InstitutionalHero
        icon={Mail}
        badge="Communication & Branding"
        title="Aperçu des emails transactionnels"
        subtitle="Prévisualisez le rendu exact des emails envoyés (identifiants, PASS FTPE, factures PADH, reset password, invitations) avec le branding O'SCOP Outremer. Aucun envoi n'est effectué."
        gradient="bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900"
        actions={
          <Button
            variant="outline"
            onClick={() => fetchPreview(selected)}
            disabled={loading}
            className="bg-white/10 hover:bg-white/20 text-white border-white/30 backdrop-blur-sm"
            data-testid="email-preview-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        {/* Sélecteur de templates */}
        <Card className="lg:col-span-1 border-slate-200" data-testid="email-preview-list">
          <CardContent className="p-4 space-y-2">
            <p className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-3">
              Templates ({templates.length})
            </p>
            {templates.map((t) => (
              <button
                key={t.key}
                onClick={() => setSelected(t.key)}
                data-testid={`email-template-${t.key}`}
                className={`w-full text-left rounded-lg border px-3 py-3 transition-all ${
                  selected === t.key
                    ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                    : 'border-slate-200 bg-white hover:bg-slate-50'
                }`}
              >
                <div className="flex items-start gap-2">
                  <Eye
                    className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      selected === t.key ? 'text-indigo-600' : 'text-slate-400'
                    }`}
                  />
                  <div className="min-w-0">
                    <p className={`text-sm font-semibold leading-tight ${
                      selected === t.key ? 'text-indigo-900' : 'text-slate-800'
                    }`}>
                      {t.label}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-1 truncate" title={t.source}>
                      {t.source}
                    </p>
                  </div>
                </div>
              </button>
            ))}
            {templates.length === 0 && !error && (
              <p className="text-sm text-slate-500 italic">Chargement…</p>
            )}
          </CardContent>
        </Card>

        {/* Aperçu */}
        <Card className="lg:col-span-3 border-slate-200" data-testid="email-preview-iframe-wrap">
          <CardContent className="p-0">
            {currentMeta && (
              <div className="border-b border-slate-200 px-5 py-3 bg-slate-50 rounded-t-lg">
                <p className="text-[11px] font-bold tracking-widest text-slate-500 uppercase">
                  Sujet
                </p>
                <p className="text-sm font-semibold text-slate-900 mt-0.5">
                  {currentMeta.subject}
                </p>
              </div>
            )}
            {error && (
              <div className="p-5 flex items-start gap-3 bg-rose-50 border-b border-rose-200">
                <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-rose-800">{error}</p>
              </div>
            )}
            <div className="bg-slate-100 p-4 lg:p-6 rounded-b-lg min-h-[600px]">
              {loading ? (
                <div className="flex items-center justify-center h-96 text-slate-400">
                  <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                  Rendu en cours…
                </div>
              ) : html ? (
                <iframe
                  title={currentMeta?.label || 'Aperçu email'}
                  srcDoc={html}
                  className="w-full bg-white rounded-md shadow-md border border-slate-200"
                  style={{ minHeight: '720px' }}
                  data-testid="email-preview-iframe"
                  sandbox=""
                />
              ) : (
                <div className="flex items-center justify-center h-96 text-slate-400">
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Sélectionnez un template à gauche
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailPreview;
